import { AxiosInstance } from 'axios';
import {
  ProposalRequest,
  ProposalResponse,
  ProfileData,
  SavedProposal,
  WebScrapeResult,
} from '../types';
import { createApiClient, CancellationManager, ApiLogger, createCancellableRequest } from '../../../services/api/apiClient';

export class ProposalService {
  private apiClient: AxiosInstance;
  private token: string;
  private cancellation: CancellationManager;
  private logger: ApiLogger;

  constructor(token: string) {
    this.token = token;
    
    // Create enhanced API client with retry and logging
    const { client, cancellation, logger } = createApiClient(
      process.env.REACT_APP_API_URL || 'http://localhost:9110',
      token,
      {
        timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),
        enableRetry: true,
        enableLogging: process.env.NODE_ENV === 'development'
      }
    );
    
    this.apiClient = client;
    this.cancellation = cancellation;
    this.logger = logger;
  }

  // Update token if needed
  updateToken(token: string): void {
    this.token = token;
    this.apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Note: getUsersWithProfiles removed - use UserBrowserSidebar's search instead
  // The /api/proposal/users/profiles endpoint is inefficient as it loads ALL users

  // Scrape website
  async scrapeWebsite(url: string): Promise<WebScrapeResult> {
    try {
      const response = await this.apiClient.post<WebScrapeResult>('/api/proposal/scrape', { url });
      return response.data;
    } catch (error) {
      console.error('Error scraping website:', error);
      throw new Error('Failed to scrape website. Please check the URL or use manual input.');
    }
  }

  // Generate proposal with cancellation support
  generateProposal(request: ProposalRequest): { promise: Promise<ProposalResponse>; cancel: () => void } {
    this.logger.info('Generating proposal', { userCount: request.user_ids.length, format: request.format_type });
    
    return createCancellableRequest(
      async (cancelToken) => {
        try {
          const response = await this.apiClient.post<ProposalResponse>(
            '/api/proposal/generate',
            request,
            { 
              cancelToken,
              timeout: 120000 // 2 minutes for generation
            }
          );
          // Map snake_case from backend to camelCase for frontend
          const proposalData = {
            proposalId: (response.data as any).proposal_id || response.data.proposalId,
            clientName: (response.data as any).client_name || response.data.clientName,
            createdAt: new Date((response.data as any).created_at || response.data.createdAt),
            profiles: response.data.profiles,
            formatType: (response.data as any).format_type || response.data.formatType
          };
          
          this.logger.info('Proposal generated successfully', { proposalId: proposalData.proposalId });
          return proposalData;
        } catch (error) {
          this.logger.error('Error generating proposal', error);
          throw new Error('Failed to generate proposal. Please try again.');
        }
      },
      this.cancellation,
      'generate-proposal'
    );
  }

  // Export proposal
  async exportProposal(proposalId: string, format: 'pptx' | 'pdf' | 'pptx-preview', profiles?: any[], formatType?: 'full' | 'one-slider' | 'intro' | 'web-bio'): Promise<Blob> {
    try {
      const payload: any = { proposal_id: proposalId };
      
      // Include profiles if provided (for immediate export without storage lookup)
      if (profiles && profiles.length > 0) {
        payload.profiles = profiles;
        this.logger.info('Exporting with profiles', { count: profiles.length, firstProfile: profiles[0] });
      } else {
        this.logger.warn('No profiles provided for export');
      }
      
      // Include format type if provided
      if (formatType) {
        payload.format_type = formatType;
        this.logger.info('Export format type', { formatType });
      }
      
      this.logger.info('Export payload', payload);
      
      const response = await this.apiClient.post(
        `/api/proposal/export/${format}`,
        payload,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting proposal:', error);
      throw new Error(`Failed to export proposal as ${format.toUpperCase()}`);
    }
  }


  // Get saved proposals
  async getSavedProposals(clientName?: string): Promise<SavedProposal[]> {
    try {
      const params = clientName ? { client_name: clientName } : {};
      const response = await this.apiClient.get<{ proposals: SavedProposal[] }>(
        '/api/proposal/proposals',
        { params }
      );
      return response.data.proposals.map(p => ({
        ...p,
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt)
      }));
    } catch (error) {
      console.error('Error fetching saved proposals:', error);
      throw new Error('Failed to fetch saved proposals');
    }
  }

  // Get specific proposal
  async getProposal(proposalId: string): Promise<ProposalResponse> {
    try {
      const metadataResponse = await this.apiClient.get(`/api/proposal/proposals/id/${proposalId}/metadata`);
      const metadata = metadataResponse.data;

      const profiles: ProfileData[] = [] as any;
      for (const userId of metadata.team_members || []) {
        try {
          const profileResponse = await this.apiClient.get(
            `/api/proposal/proposals/id/${proposalId}/profile/${userId}`,
            { params: { format_type: metadata.format } }
          );
          profiles.push(profileResponse.data);
        } catch (error) {
          console.warn(`Failed to fetch profile ${userId} for proposal ${proposalId}`, error);
        }
      }

      return {
        proposalId: metadata.proposal_id || proposalId,
        clientName: metadata.client_name,
        createdAt: new Date(metadata.created_at),
        profiles,
        formatType: metadata.format,
        metadata: {
          clientName: metadata.client_name,
          clientUrl: metadata.client_url,
          createdAt: new Date(metadata.created_at),
          updatedAt: new Date(metadata.updated_at),
          createdBy: metadata.created_by,
          teamMembers: metadata.team_members || [],
          format: metadata.format,
          scrapedContent: metadata.scraped_content
        }
      };
    } catch (error) {
      console.error('Error fetching proposal:', error);
      throw new Error('Failed to fetch proposal');
    }
  }

  // Update proposal
  async updateProposal(proposalId: string, profiles: ProfileData[]): Promise<void> {
    try {
      await this.apiClient.put(`/api/proposal/proposals/${proposalId}`, { profiles });
    } catch (error) {
      console.error('Error updating proposal:', error);
      throw new Error('Failed to update proposal');
    }
  }

  // Delete proposal
  async deleteProposal(proposalId: string): Promise<void> {
    try {
      await this.apiClient.delete(`/api/proposal/proposals/id/${proposalId}`);
    } catch (error) {
      console.error('Error deleting proposal:', error);
      throw new Error('Failed to delete proposal');
    }
  }

  // Batch operations
  async generateBatch(userIds: string[], clientContext: string, formatType: string): Promise<ProfileData[]> {
    try {
      const response = await this.apiClient.post<{ profiles: ProfileData[] }>(
        '/api/proposal/generate-batch',
        {
          user_ids: userIds,
          client_context: clientContext,
          format_type: formatType
        }
      );
      return response.data.profiles;
    } catch (error) {
      console.error('Error generating batch:', error);
      throw new Error('Failed to generate profiles in batch');
    }
  }

  // Get generation status (for async operations)
  async getGenerationStatus(taskId: string): Promise<{
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    result?: ProposalResponse;
    error?: string;
  }> {
    try {
      const response = await this.apiClient.get(`/api/proposal/status/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching generation status:', error);
      throw new Error('Failed to fetch generation status');
    }
  }

  // Cancel generation
  async cancelGeneration(taskId: string): Promise<void> {
    try {
      await this.apiClient.post(`/api/proposal/cancel/${taskId}`);
    } catch (error) {
      console.error('Error canceling generation:', error);
      throw new Error('Failed to cancel generation');
    }
  }

  // Validate URL
  async validateUrl(url: string): Promise<{ valid: boolean; accessible: boolean; error?: string }> {
    try {
      const response = await this.apiClient.post<{ valid: boolean; accessible: boolean; error?: string }>(
        '/api/proposal/validate-url',
        { url }
      );
      return response.data;
    } catch (error) {
      console.error('Error validating URL:', error);
      return { valid: false, accessible: false, error: 'Failed to validate URL' };
    }
  }

  // Get templates
  async getTemplates(): Promise<{ id: string; name: string; description: string; preview?: string }[]> {
    try {
      const response = await this.apiClient.get('/api/proposal/templates');
      return response.data.templates;
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw new Error('Failed to fetch templates');
    }
  }

  // Search proposals
  async searchProposals(query: string): Promise<SavedProposal[]> {
    try {
      const response = await this.apiClient.get<{ proposals: SavedProposal[] }>(
        '/api/proposal/search',
        { params: { q: query } }
      );
      return response.data.proposals.map(p => ({
        ...p,
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.updatedAt)
      }));
    } catch (error) {
      console.error('Error searching proposals:', error);
      throw new Error('Failed to search proposals');
    }
  }
}
